import React from "react";
import { Target, GitBranch, Users, RefreshCw } from "lucide-react";

const PRINCIPLES = [
  {
    icon: Target,
    title: "Risk-centric, not checklist-centric",
    desc: "Prioritizes threats by business impact rather than severing severity labels from operational reality.",
  },
  {
    icon: GitBranch,
    title: "Simulated, not assumed",
    desc: "Dynamic attack trees model real attacker paths with AND/OR logic across trust boundaries.",
  },
  {
    icon: Users,
    title: "Collaborative by default",
    desc: "Versioned Spaces with role-based access, visual diffs, and real-time conflict resolution.",
  },
  {
    icon: RefreshCw,
    title: "Continuous, not point-in-time",
    desc: "Re-run, version, and re-analyze every model as the architecture and code evolve.",
  },
];

export default function About() {
  return (
    <section className="tfl-about" id="about" aria-label="About ThreatForge">
      <div className="tfl-about__inner">
        <div className="tfl-about__copy">
          <div className="tfl-eyebrow">About ThreatForge</div>
          <h2 className="tfl-display">
            A threat modeling workspace built for the speed of modern software.
          </h2>

          <div className="tfl-about__body">
            <p>
              ThreatForge is an AI-augmented threat modeling workspace that keeps security
              reviews as fast as the code they protect. It metabolizes architecture diagrams,
              IaC, and API specs into components, data flows, and trust boundaries — then
              synthesizes STRIDE and PASTA threat catalogs in seconds.
            </p>
            <p style={{ marginTop: "1rem" }}>
              Security architects collaborate in versioned Spaces with role-based access,
              compare models side by side, and resolve conflicts in real time. Every decision
              lands in an auditable trail, ready to export as executive or technical reports
              across 180+ compliance frameworks.
            </p>
            <p style={{ marginTop: "1rem" }}>
              Built on proven methodologies — STRIDE for identification, DREAD for scoring,
              MITRE ATT&amp;CK for adversary grounding, and PASTA for business-aligned risk —
              ThreatForge turns threat modeling from a point-in-time exercise into a
              continuous, data-driven process.
            </p>
          </div>
        </div>

        <div className="tfl-about__panel" aria-label="ThreatForge design principles">
          <div className="tfl-about__panel-bar">
            <span>ThreatForge / principles</span>
          </div>
          <ul className="tfl-about__principles">
            {PRINCIPLES.map((p) => {
              const IconComponent = p.icon;
              return (
                <li key={p.title} className="tfl-about__principle">
                  <span className="tfl-about__principle-icon">
                    <IconComponent size={18} strokeWidth={2} />
                  </span>
                  <div>
                    <h3 className="tfl-about__principle-title">{p.title}</h3>
                    <p className="tfl-about__principle-desc">{p.desc}</p>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </section>
  );
}
